import { Phone, Mail, Sun, ShieldCheck, Users, MapPin } from "lucide-react";
import { Seo } from "@/components/Seo";
import { CtaBand, SectionHead } from "@/components/Shared";
import { SunMark } from "@/components/Sun";
import { BRAND } from "@/data/site";
import { metaAbout, orgLd } from "@/data/content";

export default function About() {
  const meta = metaAbout();
  return (
    <div data-testid="about-page">
      <Seo title={meta.title} description={meta.description} path="/about/" jsonLd={[orgLd()]} />
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 0%, rgba(245,158,11,0.12) 0%, transparent 65%)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 relative">
          <div className="flex items-center gap-4 mb-8"><SunMark size={56} /></div>
          <h1 className="text-4xl sm:text-5xl font-extrabold font-display tracking-tight text-slate-50 max-w-3xl">
            The sun never sets on our watch.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed max-w-2xl">
            {BRAND.name} is a proudly First Nation-owned and operated security guard company headquartered in {BRAND.homeBase}, protecting clients in every Canadian province and territory.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 grid lg:grid-cols-2 gap-12">
        <div>
          <SectionHead eyebrow="Our Story" title="Built in the North. Trusted across Canada." />
          <div className="space-y-5 text-slate-400 text-sm sm:text-base leading-relaxed">
            <p>
              {BRAND.name} was founded on a simple conviction: the communities being protected should share in the protection. As a First Nation-owned company, we recruit, licence and train security professionals from the communities we serve — creating skilled local employment while delivering a standard of care that outside providers cannot match.
            </p>
            <p>
              From our home base in {BRAND.homeBase}, we have grown into a national provider serving 55+ Canadian cities, from Toronto's Financial District to the work camps of the Arctic. Our officers guard construction sites, hospitals, retail floors, festivals, pipelines and government facilities with one consistent standard: licensed, insured, supervised and GPS-verified.
            </p>
            <p>
              The sun in our emblem stands for vigilance, warmth and truth — the values every officer carries onto every post, every shift, every night of the year.
            </p>
          </div>
        </div>
        <div className="space-y-4">
          {[
            { icon: Sun, t: "Community First", d: "Local hiring and cultural safety are operational requirements, not marketing lines." },
            { icon: ShieldCheck, t: "Compliance Without Compromise", d: "Provincial licensing, insurance and workers' compensation verified before every deployment." },
            { icon: Users, t: "Supervision That Shows Up", d: "Field supervisors inspect posts unannounced and report what they find — to you." },
            { icon: MapPin, t: "Coast to Coast to Coast", d: "One accountable partner for security guard services in all 13 provinces and territories." },
          ].map((v) => (
            <div key={v.t} className="flex gap-4 rounded-2xl border border-slate-800 bg-[#111827] p-6">
              <v.icon className="h-6 w-6 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-display font-bold text-slate-100 mb-1">{v.t}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{v.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-8">
        <div className="rounded-2xl border border-amber-500/25 bg-[#111827] p-8 flex flex-col md:flex-row md:items-center gap-6 justify-between">
          <div>
            <h2 className="text-xl font-bold font-display text-slate-50 mb-2">Talk to our dispatch desk directly</h2>
            <p className="text-sm text-slate-400">No forms. No queues. A real operations professional, 24/7/365.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={BRAND.phoneHref} data-testid="about-phone-link" className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-sm font-bold text-slate-950 hover:bg-amber-400 transition-colors">
              <Phone className="h-4 w-4" /> {BRAND.phone}
            </a>
            <a href={BRAND.emailHref} data-testid="about-email-link" className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-6 py-3 text-sm font-semibold text-slate-100 hover:border-amber-500/60 hover:text-amber-400 transition-colors">
              <Mail className="h-4 w-4" /> {BRAND.email}
            </a>
          </div>
        </div>
        <CtaBand heading="Partner With a First Nation-Owned Security Leader" />
      </section>
    </div>
  );
}
